/**
 * Which stage an idea (an epic request) is in, and whether it needs the user
 * (docs/new-look/PLAN.md, steps 0.5 and 2.2b). Shared so the web and the API
 * (stage filters and counts on the ideas list) always agree.
 *
 * Pure: callers pass what they have; anything left out is treated as
 * "not known", never as a guess.
 */
const ENDED = new Set(["done", "failed", "cancelled"]);
const BUILD_DISPLAY = new Set(["running", "ready", "needs_decision", "failed", "follow_up"]);
const MERGE_KINDS = new Set(["merge_approval", "merge_prs"]);
function isActive(job) {
    return !!job && !ENDED.has(job.status);
}
function isAsking(job) {
    return isActive(job) && job.status === "waiting" && job.waitingReason === "needs_input";
}
function epicIdFromPlanning(planningState) {
    if (!planningState)
        return null;
    if (typeof planningState !== "string")
        return planningState.epicId ?? null;
    try {
        const parsed = JSON.parse(planningState);
        return typeof parsed?.epicId === "string" ? parsed.epicId : null;
    }
    catch {
        return null;
    }
}
export function stageOf(facts) {
    const { request, epic, planJob, buildJob } = facts;
    if (request.status === "rejected")
        return { stage: "idea", closed: true, needsYou: null };
    const display = epic?.displayState ?? null;
    const openPrs = facts.openPullRequests ?? 0;
    const hasEpic = !!(request.convertedEpicId ?? epicIdFromPlanning(request.planningState)) || !!epic;
    // A running job, open PRs or a wrap-up keep an idea in build; so do parts
    // already done, even while nothing is running.
    const stillWorking = isActive(buildJob) || openPrs > 0 || (display !== null && BUILD_DISPLAY.has(display));
    // A merged build only finishes the idea when every part is done: a build
    // stopped early can merge with parts left (ER-130 showed "Shipped" at 1 of 4).
    // Marking the blueprint done by hand (implemented) always counts.
    const partsLeft = typeof epic?.featureCount === "number" ? Math.max(0, epic.featureCount - (epic.completedFeatureCount ?? 0)) : 0;
    const merged = display === "merged" || epic?.buildPullRequestState === "merged";
    const finished = epic?.state === "implemented" || (merged && partsLeft === 0);
    const done = finished && !stillWorking;
    const building = stillWorking || merged || (epic?.completedFeatureCount ?? 0) > 0;
    let stage;
    if (done)
        stage = "done";
    else if (building)
        stage = "build";
    else if (request.status !== "pending" || hasEpic || isActive(planJob))
        stage = "blueprint";
    else
        stage = "idea";
    return { stage, closed: false, needsYou: done ? null : needsYouOf(facts, hasEpic, openPrs, display) };
}
function needsYouOf(facts, hasEpic, openPrs, display) {
    const { request, planJob, buildJob } = facts;
    if (facts.interviewJobStatus === "awaiting_input")
        return "answer_idea_helper";
    if (isAsking(buildJob))
        return MERGE_KINDS.has(buildJob?.needsInput?.kind ?? "") ? "merge_prs" : "answer_builder";
    if (isAsking(planJob))
        return "answer_blueprint_maker";
    // Pull requests left open by a build that is no longer working on them.
    const buildWorking = isActive(buildJob) && buildJob.status !== "waiting";
    if (openPrs > 0 && !buildWorking)
        return "merge_prs";
    if (display === "needs_decision" || display === "failed")
        return "decide_build";
    if (display === "ready")
        return "review_build";
    if (request.status === "approved" && !hasEpic && !isActive(planJob))
        return "make_blueprint";
    return null;
}
//# sourceMappingURL=idea-stage.js.map